import React from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {InlineNode} from './SourcesInlineNodes';

// ─── Search highlighting helpers ─────────────────────────────────────────────

/**
 * Build a map of blockIndex → [{start, end, globalMatchIdx}] from the flat
 * searchMatches array produced by the document search service.
 */
function buildMatchesByBlock(searchMatches) {
    if (!Array.isArray(searchMatches) || !searchMatches.length) return null;
    const map = {};
    searchMatches.forEach((m, globalMatchIdx) => {
        if (!map[m.blockIndex]) map[m.blockIndex] = [];
        map[m.blockIndex].push({start: m.start, end: m.end, globalMatchIdx});
    });
    return map;
}

/**
 * Render the inline content of a flat PM node (paragraph / heading) with
 * character-range search-match highlighting applied. Works for cable nodes
 * which always have a single flat text content array.
 */
function renderWithMatches(node, matches, activeMatchIdx) {
    const inlines = node.content || [];
    if (!inlines.length) return null;

    const sorted = [...matches].sort((a, b) => a.start - b.start);
    const parts = [];
    let pos = 0;

    inlines.forEach((textNode, ti) => {
        const text = typeof textNode.text === 'string' ? textNode.text : '';
        const nodeStart = pos;
        const nodeEnd = pos + text.length;
        pos = nodeEnd;

        const overlapping = sorted.filter((r) => r.end > nodeStart && r.start < nodeEnd);
        if (!overlapping.length) {
            parts.push(<InlineNode key={`t${ti}`} node={textNode}/>);
            return;
        }

        let cur = nodeStart;
        overlapping.forEach((r, ri) => {
            const segStart = Math.max(r.start, nodeStart);
            const segEnd = Math.min(r.end, nodeEnd);
            if (segStart > cur) {
                parts.push(
                    <InlineNode key={`t${ti}-b${ri}`} node={{...textNode, text: text.slice(cur - nodeStart, segStart - nodeStart)}}/>,
                );
            }
            if (segEnd > segStart) {
                const isActive = activeMatchIdx != null && activeMatchIdx >= 0 && activeMatchIdx === r.globalMatchIdx;
                parts.push(
                    <mark
                        key={`t${ti}-m${ri}`}
                        data-search-match={r.globalMatchIdx}
                        style={{
                            backgroundColor: isActive
                                ? 'var(--search-match-active-bg, rgba(34,197,94,0.55))'
                                : 'var(--search-match-bg, rgba(134,239,172,0.45))',
                            outline: isActive ? '2px solid var(--search-match-active-ring, rgba(22,163,74,0.9))' : 'none',
                            outlineOffset: 1,
                            borderRadius: 2,
                            padding: '0 1px',
                            color: 'inherit',
                            fontWeight: isActive ? 700 : 'inherit',
                        }}
                    >
                        <InlineNode node={{...textNode, text: text.slice(segStart - nodeStart, segEnd - nodeStart)}}/>
                    </mark>,
                );
            }
            cur = segEnd;
        });
        if (cur < nodeEnd) {
            parts.push(
                <InlineNode key={`t${ti}-a`} node={{...textNode, text: text.slice(cur - nodeStart)}}/>,
            );
        }
    });

    return <>{parts}</>;
}

// ─── Highlight button constants (match PmDocContent) ─────────────────────────
const HIGHLIGHT_BTN_WIDTH  = 104;
const HIGHLIGHT_BTN_HEIGHT = 28;
const HIGHLIGHT_BTN_GAP    = 6;

function selectionAnchorRect(range) {
    const rects = range.getClientRects();
    return rects.length > 0 ? rects[0] : range.getBoundingClientRect();
}
function highlightBtnPos(anchorRect, container) {
    const cRect = container.getBoundingClientRect();
    const pad   = 4;
    const top   = anchorRect.top - cRect.top + container.scrollTop - HIGHLIGHT_BTN_HEIGHT - HIGHLIGHT_BTN_GAP;
    const left  = Math.max(pad, Math.min(
        anchorRect.right - cRect.left + container.scrollLeft - 12,
        container.clientWidth - HIGHLIGHT_BTN_WIDTH - pad,
    ));
    return {top, left};
}

// ─── Key judgment → summary node resolver ────────────────────────────────────

/**
 * Find the summary section body node whose text contains `judgmentText`.
 * The LLM stores key judgments as numbered paragraphs (e.g. "1. Coalition…")
 * so we match by substring inclusion after stripping the leading "N. " prefix.
 */
function findJudgmentNode(allNodes, judgmentText) {
    if (!judgmentText) return null;
    const needle = judgmentText.trim();
    return allNodes.find((n) => {
        if (n.attrs?.cableBodySection !== 'summary' || n.type !== 'paragraph') return false;
        const full = nodeText(n);
        return full.includes(needle);
    }) || null;
}

/** Render inline content, with optional search-match highlights */
function NodeText({node, matchesByBlock, activeMatchIdx}) {
    const inlines = node.content || [];
    if (!inlines.length) return null;
    const blockIdx = node._blockIdx;
    const matches = matchesByBlock && blockIdx != null ? matchesByBlock[blockIdx] : null;
    if (matches && matches.length) {
        return renderWithMatches(node, matches, activeMatchIdx);
    }
    return <>{inlines.map((n, i) => <InlineNode key={i} node={n}/>)}</>;
}

/** Return plain text of a node's inline children */
function nodeText(node) {
    if (!node || !node.content) return '';
    return node.content.map((n) => n.text || '').join('');
}

/** Strip "LABEL: " prefix from field text */
function stripLabel(text) {
    const idx = text.indexOf(': ');
    return idx >= 0 ? text.slice(idx + 2) : text;
}

/** All body nodes for a cableBodySection slug (with _blockIdx preserved) */
function sectionNodes(nodes, section) {
    return nodes.filter((n) => n.attrs?.cableBodySection === section);
}

// ─── Confidence dots ─────────────────────────────────────────────────────────

const CONF_COLOR = {high: '#22c55e', moderate: '#f59e0b', low: '#ef4444'};

function ConfidenceDots({confidence}) {
    const level = confidence === 'high' ? 3 : confidence === 'moderate' ? 2 : 1;
    return (
        <Stack direction="row" spacing={0.4} alignItems="center">
            {[1, 2, 3].map((i) => (
                <Box key={i} sx={{
                    width: 10, height: 10, borderRadius: '50%',
                    bgcolor: i <= level ? (CONF_COLOR[confidence] || '#6b7280') : 'grey.300',
                }}/>
            ))}
        </Stack>
    );
}

// ─── Section heading ──────────────────────────────────────────────────────────

function SectionHeading({children}) {
    return (
        <Typography variant="overline" sx={{
            display: 'block', fontWeight: 700, fontSize: '0.7rem',
            letterSpacing: '0.08em', color: 'text.secondary',
            borderBottom: '1px solid', borderColor: 'divider', pb: 0.5, mb: 1,
        }}>
            {children}
        </Typography>
    );
}

// ─── Assessment 2×2 grid ──────────────────────────────────────────────────────

const ASSESSMENTS = [
    {slug: 'political', label: 'POLITICAL'},
    {slug: 'economic',  label: 'ECONOMIC'},
    {slug: 'security',  label: 'SECURITY'},
    {slug: 'regional',  label: 'REGIONAL'},
];

function AssessmentGrid({allNodes, matchesByBlock, activeMatchIdx}) {
    const hasAny = ASSESSMENTS.some(({slug}) => sectionNodes(allNodes, slug).length > 0);
    if (!hasAny) return null;
    return (
        <Box>
            <SectionHeading>ASSESSMENT OVERVIEW</SectionHeading>
            <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5}}>
                {ASSESSMENTS.map(({slug, label}) => {
                    const nodes = sectionNodes(allNodes, slug).filter((n) => n.type !== 'heading');
                    if (!nodes.length) return null;
                    return (
                        <Box key={slug} sx={{
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 1, p: 1.5, bgcolor: 'background.paper',
                        }}>
                            <Typography variant="caption" sx={{fontWeight: 700, letterSpacing: '0.07em', color: 'primary.main', display: 'block', mb: 0.75}}>
                                {label}
                            </Typography>
                            <Stack spacing={0.5}>
                                {nodes.map((node, i) => (
                                    <Typography key={i} component="div" variant="body2" sx={{fontSize: '0.8rem', lineHeight: 1.55}}
                                        data-block-index={node._blockIdx}>
                                        <NodeText node={node} matchesByBlock={matchesByBlock} activeMatchIdx={activeMatchIdx}/>
                                    </Typography>
                                ))}
                            </Stack>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// ─── Sidebar panels ───────────────────────────────────────────────────────────

function SidebarPanel({title, icon, children}) {
    return (
        <Box sx={{mb: 2}}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{mb: 0.75}}>
                {icon && <Typography sx={{fontSize: '0.85rem'}}>{icon}</Typography>}
                <Typography variant="caption" sx={{fontWeight: 700, letterSpacing: '0.06em', color: 'text.secondary'}}>
                    {title}
                </Typography>
            </Stack>
            {children}
        </Box>
    );
}

function DistributionList({allNodes}) {
    const infoNode = allNodes.find((n) => n.attrs?.cableRoutingField === 'info');
    const distNode = allNodes.find((n) => n.attrs?.cableSignatureField === 'distribution');
    const infoItems = infoNode
        ? stripLabel(nodeText(infoNode)).split(/[;\n]/).map((s) => s.trim()).filter(Boolean) : [];
    const distItems = distNode
        ? nodeText(distNode).replace(/^DISTRIBUTION:\s*/i, '').split(/[;\n,]/).map((s) => s.trim()).filter(Boolean) : [];
    const all = [...infoItems, ...distItems];
    if (!all.length) return null;
    return (
        <Box component="ul" sx={{m: 0, pl: 2}}>
            {all.map((item, i) => (
                <Typography key={i} component="li" variant="caption" sx={{lineHeight: 1.7}}>{item}</Typography>
            ))}
        </Box>
    );
}

function TagsList({allNodes, matchesByBlock, activeMatchIdx}) {
    const tagNode = allNodes.find((n) => n.attrs?.cableRole === 'tags');
    if (!tagNode) return null;
    const raw = nodeText(tagNode).replace(/^TAGS:\s*/i, '');
    const parsed = [];
    const re = /([A-Z0-9]{2,6})\s+\(([^)]+)\)/g;
    let m;
    while ((m = re.exec(raw)) !== null) parsed.push({code: m[1], desc: m[2]});
    if (!parsed.length) raw.split(/\s+/).filter(Boolean).forEach((c) => parsed.push({code: c, desc: null}));

    // Render as parsed pairs, but stamp a hidden full-node mark wrapper so search
    // matches inside the TAGS block show a highlighted border on the panel.
    const hasMatch = matchesByBlock && tagNode._blockIdx != null && matchesByBlock[tagNode._blockIdx];
    return (
        <Box data-block-index={tagNode._blockIdx}
            sx={hasMatch ? {outline: '2px solid var(--search-match-active-bg, rgba(34,197,94,0.55))', borderRadius: 1} : undefined}>
            <Stack spacing={0.25}>
                {parsed.map(({code, desc}, i) => (
                    <Stack key={i} direction="row" alignItems="center" spacing={0.75}>
                        <Typography variant="caption" sx={{fontFamily: 'monospace', fontWeight: 700, minWidth: 40, color: 'primary.main'}}>{code}</Typography>
                        {desc && <Typography variant="caption" sx={{color: 'text.secondary'}}>{desc}</Typography>}
                    </Stack>
                ))}
            </Stack>
            {/* Hidden span carries data-search-match marks for scroll-to behaviour */}
            {hasMatch && (
                <Box component="span" aria-hidden="true" sx={{position: 'absolute', opacity: 0, pointerEvents: 'none', overflow: 'hidden', width: 0, height: 0}}>
                    <NodeText node={tagNode} matchesByBlock={matchesByBlock} activeMatchIdx={activeMatchIdx}/>
                </Box>
            )}
        </Box>
    );
}

function ReferencesList({allNodes, matchesByBlock, activeMatchIdx}) {
    const refNode = allNodes.find((n) => n.attrs?.cableRole === 'ref');
    if (!refNode) return null;
    return (
        <Box data-block-index={refNode._blockIdx}>
            <Typography component="div" variant="caption" sx={{lineHeight: 1.7}}>
                <NodeText node={refNode} matchesByBlock={matchesByBlock} activeMatchIdx={activeMatchIdx}/>
            </Typography>
        </Box>
    );
}

function AttachmentsList({allNodes, matchesByBlock, activeMatchIdx}) {
    const attNode = allNodes.find((n) => n.attrs?.cableSignatureField === 'attachments');
    if (!attNode) return null;
    return (
        <Box data-block-index={attNode._blockIdx}>
            <Typography component="div" variant="caption" sx={{lineHeight: 1.7}}>
                <NodeText node={attNode} matchesByBlock={matchesByBlock} activeMatchIdx={activeMatchIdx}/>
            </Typography>
        </Box>
    );
}

function CoordinationPanel({allNodes, matchesByBlock, activeMatchIdx}) {
    const fields = [
        {field: 'drafted_by', label: 'DRAFTED BY'},
        {field: 'cleared_by', label: 'CLEARED BY'},
        {field: 'approved_by', label: 'APPROVED BY'},
        {field: 'released_by', label: 'RELEASED BY'},
    ];
    const present = fields.filter(({field}) => allNodes.some((n) => n.attrs?.cableSignatureField === field));
    if (!present.length) return null;
    return (
        <Stack spacing={1}>
            {present.map(({field, label}) => {
                const node = allNodes.find((n) => n.attrs?.cableSignatureField === field);
                if (!node) return null;
                return (
                    <Box key={field} data-block-index={node._blockIdx}>
                        <Typography variant="caption" sx={{fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em'}}>{label}:</Typography>
                        <Typography component="div" variant="caption" sx={{pl: 1, lineHeight: 1.6}}>
                            <NodeText node={node} matchesByBlock={matchesByBlock} activeMatchIdx={activeMatchIdx}/>
                        </Typography>
                    </Box>
                );
            })}
        </Stack>
    );
}

// ─── Key judgments ────────────────────────────────────────────────────────────

function KeyJudgmentsPanel({judgments, allNodes, matchesByBlock, activeMatchIdx}) {
    if (!judgments || !judgments.length) return null;
    return (
        <Box sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: 'background.paper'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
                <Typography variant="caption" sx={{fontWeight: 700, letterSpacing: '0.07em'}}>KEY JUDGMENTS</Typography>
                <Typography variant="caption" sx={{color: 'text.disabled'}}>Confidence</Typography>
            </Stack>
            <Stack spacing={0.75}>
                {judgments.map((j, i) => {
                    const node = findJudgmentNode(allNodes, j.text);
                    return (
                        <Stack key={i} direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}
                            data-block-index={node?._blockIdx}>
                            <Typography component="div" variant="caption" sx={{flex: 1, lineHeight: 1.5}}>
                                {node
                                    ? <NodeText node={node} matchesByBlock={matchesByBlock} activeMatchIdx={activeMatchIdx}/>
                                    : j.text}
                            </Typography>
                            <Box sx={{flexShrink: 0, pt: 0.2}}><ConfidenceDots confidence={j.confidence}/></Box>
                        </Stack>
                    );
                })}
            </Stack>
        </Box>
    );
}

// ─── Executive summary ────────────────────────────────────────────────────────

function ExecutiveSummary({allNodes, matchesByBlock, activeMatchIdx}) {
    const bodyNodes = sectionNodes(allNodes, 'summary').filter((n) => n.type !== 'heading');
    if (!bodyNodes.length) return null;
    return (
        <Box>
            <SectionHeading>EXECUTIVE SUMMARY</SectionHeading>
            <Stack spacing={0.5}>
                {bodyNodes.map((node, i) => (
                    <Typography key={i} component="div" variant="body2" sx={{lineHeight: 1.6}} data-block-index={node._blockIdx}>
                        <NodeText node={node} matchesByBlock={matchesByBlock} activeMatchIdx={activeMatchIdx}/>
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
}

// ─── Other body sections ──────────────────────────────────────────────────────

function BodySections({allNodes, matchesByBlock, activeMatchIdx}) {
    const SKIP = new Set(['summary', 'political', 'economic', 'security', 'regional']);
    const sections = [];
    let cur = null;

    allNodes.forEach((node) => {
        const slug = node.attrs?.cableBodySection;
        if (!slug || SKIP.has(slug)) return;
        if (slug !== cur?.slug) {
            cur = {slug, nodes: []};
            sections.push(cur);
        }
        cur.nodes.push(node);
    });

    if (!sections.length) return null;
    return (
        <Stack spacing={2}>
            {sections.map(({slug, nodes}) => {
                const heading = nodes.find((n) => n.type === 'heading');
                const body = nodes.filter((n) => n.type !== 'heading');
                return (
                    <Box key={slug}>
                        <SectionHeading>
                            {heading ? nodeText(heading) : slug.replace(/_/g, ' ').toUpperCase()}
                        </SectionHeading>
                        <Stack spacing={0.5}>
                            {body.map((node, i) => (
                                <Typography key={i} component="div" variant="body2" sx={{lineHeight: 1.6}} data-block-index={node._blockIdx}>
                                    <NodeText node={node} matchesByBlock={matchesByBlock} activeMatchIdx={activeMatchIdx}/>
                                </Typography>
                            ))}
                        </Stack>
                    </Box>
                );
            })}
        </Stack>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Rich cable-format viewer. Renders when `doc.attrs.cableDoc === true`.
 *
 * Search is fully supported: `searchMatches` (array of {blockIndex, start, end})
 * and `activeSearchMatchIdx` are used to highlight matching text ranges in-place
 * with `<mark data-search-match>` elements, and the active match is scrolled into
 * view automatically — matching the behaviour of `ReadonlyProseMirrorRenderer`.
 */
export default function CableDocViewer({pmDoc, searchMatches = null, activeSearchMatchIdx = -1, onBlockHighlight = null}) {
    const containerRef = React.useRef(null);
    const [selBtn, setSelBtn] = React.useState(null); // {top, left, blockIndex, startOffset, endOffset, text}
    const canHighlight = typeof onBlockHighlight === 'function';

    const allNodes = React.useMemo(
        () => (pmDoc?.content || []).map((node, idx) => ({...node, _blockIdx: idx})),
        [pmDoc],
    );

    const matchesByBlock = React.useMemo(
        () => buildMatchesByBlock(searchMatches),
        [searchMatches],
    );

    // Scroll active search match into view (mirrors PmDocContent's scroll logic).
    React.useEffect(() => {
        if (activeSearchMatchIdx == null || activeSearchMatchIdx < 0) return;
        let cancelled = false;
        let attempts = 0;

        function attempt() {
            if (cancelled) return;
            const el = containerRef.current?.querySelector(`[data-search-match="${activeSearchMatchIdx}"]`);
            if (el) {
                el.scrollIntoView({behavior: 'smooth', block: 'center'});
                return;
            }
            if (++attempts < 15) requestAnimationFrame(attempt);
        }
        requestAnimationFrame(attempt);
        return () => { cancelled = true; };
    }, [activeSearchMatchIdx]);

    // ── Floating "★ Highlight" button on text selection ──────────────────────
    React.useEffect(() => {
        if (!canHighlight) return;
        const container = containerRef.current;
        if (!container) return;
        const handleUp = () => {
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed || !sel.rangeCount) { setSelBtn(null); return; }
            const range = sel.getRangeAt(0);
            if (!container.contains(range.commonAncestorContainer)) { setSelBtn(null); return; }
            const selectedText = sel.toString().trim();
            if (!selectedText) { setSelBtn(null); return; }
            // Walk up to find the nearest [data-block-index] ancestor
            let el = range.startContainer.nodeType === 3
                ? range.startContainer.parentElement
                : range.startContainer;
            while (el && el !== container) {
                if (el.dataset?.blockIndex != null) break;
                el = el.parentElement;
            }
            const blockIndex = el && el.dataset?.blockIndex != null ? Number(el.dataset.blockIndex) : -1;
            // Compute character offsets within the block element's full text
            const fullText = el ? el.textContent || '' : '';
            const beforeRange = document.createRange();
            if (el) {
                beforeRange.setStart(el, 0);
                beforeRange.setEnd(range.startContainer, range.startOffset);
            }
            const startOffset = beforeRange.toString().length;
            const endOffset   = startOffset + selectedText.length;
            const anchorRect = selectionAnchorRect(range);
            const {top, left} = highlightBtnPos(anchorRect, container);
            setSelBtn({top, left, blockIndex, startOffset, endOffset, text: selectedText, fullText});
        };
        const handleChange = () => {
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed) setSelBtn(null);
        };
        container.addEventListener('mouseup', handleUp);
        document.addEventListener('selectionchange', handleChange);
        return () => {
            container.removeEventListener('mouseup', handleUp);
            document.removeEventListener('selectionchange', handleChange);
        };
    }, [canHighlight]);

    if (!pmDoc || pmDoc.attrs?.cableDoc !== true) return null;

    const attrs = pmDoc.attrs || {};

    const classification = attrs.classification || '';
    const keyJudgments  = Array.isArray(attrs.keyJudgments) ? attrs.keyJudgments : [];

    const fieldText = (routingField) =>
        stripLabel(nodeText(allNodes.find((n) => n.attrs?.cableRoutingField === routingField) || {}))
        || attrs[routingField] || '';

    const dtg         = fieldText('dtg');
    const precedence  = fieldText('precedence');
    const origin      = fieldText('origin');
    const destination = fieldText('destination');
    const cableNumber = fieldText('cable_number');
    const classReason = stripLabel(nodeText(allNodes.find((n) => n.attrs?.cableRole === 'eo_decl') || {}))
                        || attrs.classificationReason || '';
    const subject     = attrs.subject
                        || stripLabel(nodeText(allNodes.find((n) => n.attrs?.cableRole === 'subject') || {}));

    const classUpper = classification.toUpperCase();
    const classColor = classUpper.includes('TOP SECRET') ? '#7c3aed'
        : classUpper.includes('SECRET')       ? '#b91c1c'
        : classUpper.includes('CONFIDENTIAL') ? '#d97706'
        : classUpper.includes('UNCLASSIFIED') ? '#16a34a'
        : '#374151';

    const searchVars = {
        '--search-match-bg':          'rgba(134,239,172,0.45)',
        '--search-match-active-bg':   'rgba(34,197,94,0.55)',
        '--search-match-active-ring': 'rgba(22,163,74,0.9)',
    };

    return (
        <Box ref={containerRef} sx={{fontFamily: 'inherit', fontSize: '0.875rem', minWidth: 0, position: 'relative', ...searchVars}}>

            {/* Floating "★ Highlight" button on text selection */}
            {canHighlight && selBtn && (
                <Box
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                        onBlockHighlight({
                            blockIndex:  selBtn.blockIndex,
                            startOffset: selBtn.startOffset,
                            endOffset:   selBtn.endOffset,
                            text:        selBtn.text,
                        });
                        window.getSelection()?.removeAllRanges();
                        setSelBtn(null);
                    }}
                    sx={{
                        position: 'absolute',
                        top:    selBtn.top,
                        left:   selBtn.left,
                        width:  HIGHLIGHT_BTN_WIDTH,
                        height: HIGHLIGHT_BTN_HEIGHT,
                        zIndex: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: 'primary.main', color: '#fff',
                        borderRadius: '14px',
                        fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.04em',
                        cursor: 'pointer',
                        boxShadow: 3,
                        userSelect: 'none',
                        '&:hover': {bgcolor: 'primary.dark'},
                    }}
                >
                    ★ Highlight
                </Box>
            )}

            {/* CLASSIFICATION BANNER */}
            <Box sx={{
                bgcolor: classColor, color: '#fff', py: 0.75, px: 2,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderRadius: '4px 4px 0 0',
            }}>
                <Typography sx={{fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.8rem'}}>
                    # {classification}
                </Typography>
                <Typography sx={{fontFamily: 'monospace', fontWeight: 600, fontSize: '0.75rem', opacity: 0.9}}>
                    EMBASSY CABLE
                </Typography>
            </Box>

            {/* METADATA HEADER GRID */}
            <Box sx={{
                display: 'grid', gridTemplateColumns: '1.6fr 1.4fr 1fr 1.2fr 1.6fr',
                border: '1px solid', borderColor: 'divider', borderTop: 'none', bgcolor: 'grey.50',
            }}>
                {[
                    {label: 'CABLE NUMBER',    field: 'cable_number'},
                    {label: 'DATE / TIME (UTC)', field: 'dtg'},
                    {label: 'PRECEDENCE',      field: 'precedence'},
                    {label: 'ORIGIN',          field: 'origin'},
                    {label: 'DESTINATION',     field: 'destination'},
                ].map(({label, field}, i) => {
                    const node = allNodes.find((n) => n.attrs?.cableRoutingField === field);
                    return (
                        <Box key={i} sx={{p: 1, borderRight: i < 4 ? '1px solid' : 'none', borderColor: 'divider'}}
                            data-block-index={node?._blockIdx}>
                            <Typography variant="caption" sx={{fontWeight: 700, display: 'block', color: 'text.secondary', mb: 0.25, fontSize: '0.65rem', letterSpacing: '0.05em'}}>
                                {label}:
                            </Typography>
                            <Typography component="div" variant="body2" sx={{fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.3}}>
                                {node
                                    ? <NodeText node={node} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/>
                                    : (attrs[field] || '—')}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* CLASSIFICATION REASON */}
            {(() => {
                const eoNode = allNodes.find((n) => n.attrs?.cableRole === 'eo_decl');
                if (!eoNode && !classReason) return null;
                return (
                    <Box sx={{border: '1px solid', borderColor: 'divider', borderTop: 'none', px: 1.5, py: 0.5, bgcolor: 'grey.50'}}
                        data-block-index={eoNode?._blockIdx}>
                        <Typography component="div" variant="caption" sx={{fontFamily: 'monospace', color: 'text.secondary'}}>
                            {eoNode
                                ? <NodeText node={eoNode} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/>
                                : <>CLASSIFICATION REASON: <strong>{classReason}</strong></>}
                        </Typography>
                    </Box>
                );
            })()}

            {/* TWO-COLUMN BODY */}
            <Box sx={{
                display: 'grid', gridTemplateColumns: '200px 1fr',
                border: '1px solid', borderColor: 'divider', borderTop: 'none', minHeight: 400,
            }}>
                {/* SIDEBAR */}
                <Box sx={{borderRight: '1px solid', borderColor: 'divider', p: 1.5, bgcolor: 'grey.50', overflowY: 'auto'}}>
                    <SidebarPanel title="DISTRIBUTION" icon="👥"><DistributionList allNodes={allNodes}/></SidebarPanel>
                    <SidebarPanel title="TAGS" icon="🏷"><TagsList allNodes={allNodes} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/></SidebarPanel>
                    <SidebarPanel title="REFERENCES" icon="📖"><ReferencesList allNodes={allNodes} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/></SidebarPanel>
                    <SidebarPanel title="ATTACHMENTS" icon="📎"><AttachmentsList allNodes={allNodes} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/></SidebarPanel>
                    <SidebarPanel title="COORDINATION" icon="👤"><CoordinationPanel allNodes={allNodes} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/></SidebarPanel>
                </Box>

                {/* MAIN CONTENT */}
                <Box sx={{p: 2, overflowY: 'auto'}}>

                    {/* SUBJECT */}
                    {(() => {
                        const subjectNode = allNodes.find((n) => n.attrs?.cableRole === 'subject');
                        if (!subjectNode && !subject) return null;
                        return (
                            <Box sx={{mb: 2, pb: 1.5, borderBottom: '2px solid', borderColor: 'primary.light'}}
                                data-block-index={subjectNode?._blockIdx}>
                                <Typography variant="caption" sx={{fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.5, letterSpacing: '0.07em'}}>
                                    SUBJECT:
                                </Typography>
                                <Typography component="div" variant="h6" sx={{fontWeight: 600, lineHeight: 1.35, fontSize: '1rem'}}>
                                    {subjectNode
                                        ? <NodeText node={subjectNode} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/>
                                        : subject}
                                </Typography>
                            </Box>
                        );
                    })()}

                    {/* EXECUTIVE SUMMARY + KEY JUDGMENTS */}
                    <Box sx={{display: 'grid', gridTemplateColumns: keyJudgments.length ? '3fr 2fr' : '1fr', gap: 2, mb: 2}}>
                        <ExecutiveSummary allNodes={allNodes} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/>
                        {keyJudgments.length > 0 && <KeyJudgmentsPanel judgments={keyJudgments} allNodes={allNodes} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/>}
                    </Box>

                    {/* ASSESSMENT 2×2 GRID */}
                    <Box sx={{mb: 2}}>
                        <AssessmentGrid allNodes={allNodes} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/>
                    </Box>

                    {/* OTHER SECTIONS */}
                    <Box sx={{mb: 2}}>
                        <BodySections allNodes={allNodes} matchesByBlock={matchesByBlock} activeMatchIdx={activeSearchMatchIdx}/>
                    </Box>

                </Box>
            </Box>

            {/* END CABLE FOOTER */}
            <Box sx={{
                border: '1px solid', borderColor: 'divider', borderTop: 'none',
                display: 'flex', justifyContent: 'space-between',
                px: 2, py: 0.75, bgcolor: 'grey.100', borderRadius: '0 0 4px 4px',
            }}>
                <Typography variant="caption" sx={{fontFamily: 'monospace', fontWeight: 600, color: 'text.secondary'}}>END CABLE</Typography>
                <Typography variant="caption" sx={{fontFamily: 'monospace', fontWeight: 700, color: 'text.secondary'}}>NNNN</Typography>
            </Box>

        </Box>
    );
}
